import { ActionLogEntry, ActionLogEntryAuthor, CombinedLog } from '../redux/reducers/api-types';

/**
 * Severity + label mapping for ecs-action-log dispatch events, ported from monitorix-frontend
 * (`src/utils/actionLogLevel.ts`). Trimmed to what bo-new needs: debug filtering and event labels.
 * No color tiers, no per-event French descriptions, no actor-name resolution.
 */

/** Severity tiers for ride action log entries. `debug` rows are omitted from the combined history. */
export type ActionLogSeverity = 'debug' | 'minor' | 'info' | 'warning' | 'error' | 'critical';

export type ActionLogDisplayLevel = Exclude<ActionLogSeverity, 'debug'>;

/** UI strings for the dispatch-event "event" column. */
export const ACTION_LOG_LABELS = {
    dispatchSearchStarted: "Démarrage d'une recherche",
    plannedRetry: 'Relance prévue',
    warning: 'Warning',
    monitoring: '(En surveillance)',
    dispatchAutoFinished: 'Dispatch arrêté',
    dispatchResumeFailed: 'Échec reprise dispatch',
    dispatchResignedCandidatesExhausted: 'Dispatch abandonné',
} as const;

/**
 * Orchestrator / workflow events treated as debug noise (hidden from ride history lists).
 * Keep in sync with product expectations for filtered-out low-signal lines.
 */
export const ACTION_LOG_DEBUG_EVENT_NAMES = new Set<string>([
    'workflow.execute.started',
    'orchestrator.workflow.selected',
    'orchestrator.dispatch-timing.approved',
    'orchestrator.external-event.received',
    'orchestrator.ride-patch.received',
    'orchestrator.dispatch-initiation.skipped-active-workflow',
    'orchestrator.workflow-event.ignored',
]);

/** `payload.reason` / `payload.important.reason` from API. */
export function getDispatchFailureReason(payload: unknown): string | undefined {
    if (typeof payload !== 'object' || payload === null) {
        return undefined;
    }
    const p = payload as { reason?: string; important?: { reason?: string } };
    return p.reason ?? p.important?.reason;
}

function lowerReason(reason: unknown): string | undefined {
    if (typeof reason !== 'string') {
        return undefined;
    }
    return reason.toLowerCase();
}

export function isDispatchRetryScheduledNoEligibleDriversReason(reason: unknown): boolean {
    const r = lowerReason(reason);
    return r !== undefined && r.includes('no eligible drivers') && r.includes('retry');
}

const DISPATCH_FAILED_NO_ELIGIBLE_EXACT = 'no eligible drivers available in current workflow run';

export function isNoEligibleDriversDispatchFailure(reason: unknown): boolean {
    if (typeof reason !== 'string') {
        return false;
    }
    return reason === DISPATCH_FAILED_NO_ELIGIBLE_EXACT || reason.includes('no eligible drivers available');
}

/** `payload.nextAction` when the failed wave resigns instead of scheduling a retry. */
export function isDispatchFailedFollowedByResign(payload: unknown): boolean {
    return (
        typeof payload === 'object' &&
        payload !== null &&
        (payload as { nextAction?: string }).nextAction === 'resign-candidate-pool-exhausted'
    );
}

const INIT_FAILED_DISPATCH_NOT_ALLOWED = 'DispatchInitiationNotAllowedError';

export function isInitFailedDispatchNotAllowedPayload(payload: unknown): boolean {
    return (
        typeof payload === 'object' &&
        payload !== null &&
        (payload as { error?: { name?: string } }).error?.name === INIT_FAILED_DISPATCH_NOT_ALLOWED
    );
}

/** Emitted by Core for each processed FlightStats alert, per linked ride (LT-2042). */
export const FLIGHTSTATS_ALERT_RECEIVED = 'flightstats_alert_received';

/** Payload contract shared by convention with webservice (FlightDelayedTripConnection). */
export type FlightStatsAlertPayload = {
    flight_number?: string;
    airline?: string;
    alert_received_at?: string;
    scheduled_arrival?: string;
    updated_arrival?: string;
    delay_minutes?: number;
    flight_status?: string;
    applied?: boolean;
    new_pickup_time?: string | null;
    not_applied_reason?: string | null;
};

export function getFlightStatsAlertPayload(payload: unknown): FlightStatsAlertPayload | undefined {
    if (typeof payload !== 'object' || payload === null || Array.isArray(payload)) {
        return undefined;
    }
    return payload as FlightStatsAlertPayload;
}

const FLIGHTSTATS_NOT_APPLIED_REASONS: Record<string, string> = {
    already_allocated: 'course déjà affectée à un chauffeur',
    ride_cancelled: 'course annulée',
    subcontracted: 'course sous-traitée',
    below_threshold: 'retard sous le seuil de décalage automatique',
    out_of_window: 'vol trop éloigné dans le temps',
    no_significant_change: 'prise en charge déjà à jour',
    ride_not_updatable: 'course non modifiable',
};

export function formatFlightStatsNotAppliedReason(reason: string | null | undefined): string {
    if (!reason) {
        return 'raison inconnue';
    }
    return FLIGHTSTATS_NOT_APPLIED_REASONS[reason] ?? reason;
}

const FLIGHTSTATS_STATUS_LABELS: Record<string, string> = {
    active: 'en vol',
    cancelled: 'annulé',
    diverted: 'dérouté',
    landed: 'atterri',
    redirected: 'redirigé',
    scheduled: 'programmé',
    other: 'autre',
};

export function formatFlightStatsStatus(status: string | null | undefined): string | undefined {
    if (!status) {
        return undefined;
    }
    return FLIGHTSTATS_STATUS_LABELS[status] ?? status;
}

function formatIsoTime(iso: string | null | undefined): string | undefined {
    if (!iso) {
        return undefined;
    }
    const date = new Date(iso);
    if (isNaN(date.getTime())) {
        return undefined;
    }
    return date.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
}

/**
 * One-line summary for a FlightStats alert entry: flight + delay, then the impact on THIS ride
 * (new pickup time, or the reason why nothing was applied — the case support cares most about).
 */
export function describeFlightStatsAlert(rawPayload: unknown): string {
    const p = getFlightStatsAlertPayload(rawPayload);
    if (!p) {
        return 'Alerte FlightStats reçue';
    }
    const flight = p.flight_number ? ` — vol ${p.flight_number}` : '';
    const delay =
        typeof p.delay_minutes === 'number' && p.delay_minutes > 0
            ? ` retardé de ${p.delay_minutes} min`
            : ' à l’heure';
    const arrival =
        formatIsoTime(p.scheduled_arrival) && formatIsoTime(p.updated_arrival)
            ? ` (arrivée ${formatIsoTime(p.scheduled_arrival)} → ${formatIsoTime(p.updated_arrival)})`
            : '';
    const impact = p.applied
        ? ` : prise en charge décalée à ${formatIsoTime(p.new_pickup_time) ?? '?'}`
        : ` : non appliquée (${formatFlightStatsNotAppliedReason(p.not_applied_reason)})`;
    return `Alerte FlightStats reçue${flight}${delay}${arrival}${impact}`;
}

/** Label/value lines shown in the expanded row for a FlightStats alert. */
export function getFlightStatsAlertDetails(rawPayload: unknown): Array<{ label: string; value: string }> {
    const p = getFlightStatsAlertPayload(rawPayload);
    if (!p) {
        return [];
    }
    const lines: Array<{ label: string; value: string }> = [];
    if (p.flight_number) {
        lines.push({ label: 'Vol', value: `${p.flight_number}${p.airline ? ` (${p.airline})` : ''}` });
    }
    const status = formatFlightStatsStatus(p.flight_status);
    if (status) {
        lines.push({ label: 'Statut du vol', value: status });
    }
    if (p.scheduled_arrival) {
        lines.push({ label: 'Arrivée prévue', value: formatIsoTime(p.scheduled_arrival) ?? p.scheduled_arrival });
    }
    if (p.updated_arrival) {
        lines.push({ label: 'Arrivée estimée', value: formatIsoTime(p.updated_arrival) ?? p.updated_arrival });
    }
    if (typeof p.delay_minutes === 'number') {
        lines.push({ label: 'Retard', value: `${p.delay_minutes} min` });
    }
    if (p.applied) {
        lines.push({
            label: 'Prise en charge',
            value: `décalée à ${formatIsoTime(p.new_pickup_time) ?? p.new_pickup_time ?? '?'}`,
        });
    } else {
        lines.push({
            label: 'Prise en charge',
            value: `inchangée — ${formatFlightStatsNotAppliedReason(p.not_applied_reason)}`,
        });
    }
    return lines;
}

/**
 * Emitted by Core (CheckUnconfirmedFlightDeparturesCommand) when a flight is still "scheduled" well
 * after its last known departure time: FlightStats has gone silent, the flight is most likely late.
 */
export const FLIGHTSTATS_DEPARTURE_UNCONFIRMED = 'flightstats_departure_unconfirmed';

/** Payload contract shared by convention with webservice (CheckUnconfirmedFlightDeparturesCommand). */
export type FlightStatsDepartureUnconfirmedPayload = {
    flight_number?: string;
    airline?: string;
    flight_status?: string | null;
    scheduled_departure?: string | null;
    last_known_departure?: string | null;
    scheduled_arrival?: string | null;
    minutes_overdue?: number;
    threshold_minutes?: number;
    checked_at?: string;
};

export function describeFlightStatsDepartureUnconfirmed(rawPayload: unknown): string {
    const p = getFlightStatsAlertPayload(rawPayload) as FlightStatsDepartureUnconfirmedPayload | undefined;
    const flight = p?.flight_number ? `Vol ${p.flight_number}` : 'Vol';
    const departure = formatIsoTime(p?.last_known_departure);
    const overdue = typeof p?.minutes_overdue === 'number' ? `${p.minutes_overdue} min après` : 'après';
    const since = departure ? `${overdue} son départ prévu (${departure})` : `${overdue} son départ prévu`;
    return `${flight} toujours annoncé « programmé » ${since}, sans info FlightStats : retard probable, arrivée à vérifier`;
}

export function getFlightStatsDepartureUnconfirmedDetails(
    rawPayload: unknown,
): Array<{ label: string; value: string }> {
    const p = getFlightStatsAlertPayload(rawPayload) as FlightStatsDepartureUnconfirmedPayload | undefined;
    if (!p) {
        return [];
    }
    const lines: Array<{ label: string; value: string }> = [];
    if (p.flight_number) {
        lines.push({ label: 'Vol', value: `${p.flight_number}${p.airline ? ` (${p.airline})` : ''}` });
    }
    lines.push({ label: 'Statut du vol', value: formatFlightStatsStatus(p.flight_status) ?? 'aucun statut reçu' });
    if (p.scheduled_departure) {
        lines.push({ label: 'Départ prévu', value: formatIsoTime(p.scheduled_departure) ?? p.scheduled_departure });
    }
    if (p.last_known_departure && p.last_known_departure !== p.scheduled_departure) {
        lines.push({
            label: 'Dernier départ estimé',
            value: formatIsoTime(p.last_known_departure) ?? p.last_known_departure,
        });
    }
    if (p.scheduled_arrival) {
        lines.push({ label: 'Arrivée prévue', value: formatIsoTime(p.scheduled_arrival) ?? p.scheduled_arrival });
    }
    if (typeof p.minutes_overdue === 'number') {
        lines.push({ label: 'Départ non confirmé depuis', value: `${p.minutes_overdue} min` });
    }
    return lines;
}

/** Label/value lines for any FlightStats dispatch event; empty for other events. */
export function getFlightStatsEventDetails(
    eventName: string,
    rawPayload: unknown,
): Array<{ label: string; value: string }> {
    if (eventName === FLIGHTSTATS_ALERT_RECEIVED) {
        return getFlightStatsAlertDetails(rawPayload);
    }
    if (eventName === FLIGHTSTATS_DEPARTURE_UNCONFIRMED) {
        return getFlightStatsDepartureUnconfirmedDetails(rawPayload);
    }
    return [];
}

export type ActionLogRowUi = {
    displayLevel: ActionLogDisplayLevel;
    /** Label shown for the dispatch-event row (e.g. Warning, Event: …). */
    eventColumnLabel: string;
};

function eventColumnLabelFallback(eventName: string): string {
    return `Event: ${eventName}`;
}

/** Single place for event label + severity tier (non-debug entries only). */
export function getActionLogRowUi(eventName: string, payload: unknown): ActionLogRowUi {
    const reason = getDispatchFailureReason(payload);

    if (eventName === 'orchestrator.dispatch-retry.terminal') {
        return { displayLevel: 'critical', eventColumnLabel: ACTION_LOG_LABELS.dispatchAutoFinished };
    }
    if (eventName === 'orchestrator.dispatch.resigned-candidates-exhausted') {
        return {
            displayLevel: 'critical',
            eventColumnLabel: ACTION_LOG_LABELS.dispatchResignedCandidatesExhausted,
        };
    }
    if (eventName === 'orchestrator.dispatch-resume.failed') {
        return { displayLevel: 'error', eventColumnLabel: ACTION_LOG_LABELS.dispatchResumeFailed };
    }
    if (eventName === 'orchestrator.dispatch-retry.scheduled') {
        if (isDispatchRetryScheduledNoEligibleDriversReason(reason)) {
            return { displayLevel: 'warning', eventColumnLabel: ACTION_LOG_LABELS.warning };
        }
        return { displayLevel: 'minor', eventColumnLabel: ACTION_LOG_LABELS.monitoring };
    }
    if (eventName === 'orchestrator.dispatch.failed') {
        // No retry follows when the wave failed with an exhausted candidate pool (a resign entry
        // comes right after) — "Relance prévue" would be wrong.
        if (isDispatchFailedFollowedByResign(payload)) {
            return { displayLevel: 'warning', eventColumnLabel: ACTION_LOG_LABELS.warning };
        }
        if (isNoEligibleDriversDispatchFailure(reason)) {
            return { displayLevel: 'info', eventColumnLabel: ACTION_LOG_LABELS.plannedRetry };
        }
        return { displayLevel: 'warning', eventColumnLabel: ACTION_LOG_LABELS.warning };
    }
    if (eventName === 'orchestrator.dispatch.no-driver-alarm') {
        return { displayLevel: 'warning', eventColumnLabel: ACTION_LOG_LABELS.warning };
    }
    if (eventName === 'orchestrator.dispatch-initiated') {
        return { displayLevel: 'info', eventColumnLabel: ACTION_LOG_LABELS.dispatchSearchStarted };
    }
    if (eventName === FLIGHTSTATS_ALERT_RECEIVED) {
        return { displayLevel: 'info', eventColumnLabel: 'Alerte FlightStats' };
    }
    if (eventName === FLIGHTSTATS_DEPARTURE_UNCONFIRMED) {
        return { displayLevel: 'warning', eventColumnLabel: ACTION_LOG_LABELS.warning };
    }
    if (eventName === 'orchestrator.init.failed' && isInitFailedDispatchNotAllowedPayload(payload)) {
        return { displayLevel: 'warning', eventColumnLabel: ACTION_LOG_LABELS.warning };
    }
    return { displayLevel: 'info', eventColumnLabel: eventColumnLabelFallback(eventName) };
}

/**
 * A {@link CombinedLog} is an {@link ActionLogEntry} iff it carries `event_name`.
 * (bo-new's legacy `History` has no such field — unlike monitorix's `actionLog` wrapper.)
 */
export function isActionLogEntry(log: CombinedLog): log is ActionLogEntry {
    return 'event_name' in log;
}

/**
 * Full severity including `debug` (for filtering). Same rules as display level except `debug`
 * comes only from {@link ACTION_LOG_DEBUG_EVENT_NAMES}.
 */
export function getActionLogSeverity(eventName: string, payload: unknown): ActionLogSeverity {
    if (ACTION_LOG_DEBUG_EVENT_NAMES.has(eventName)) {
        return 'debug';
    }
    return getActionLogRowUi(eventName, payload).displayLevel;
}

export function getActionLogDisplayLevel(eventName: string, payload: unknown): ActionLogDisplayLevel {
    return getActionLogRowUi(eventName, payload).displayLevel;
}

/** Drops dispatch entries at `debug` severity; keeps all legacy history rows. */
export function excludingDebugActionLogs(entries: CombinedLog[]): CombinedLog[] {
    return entries.filter((log) => {
        if (!isActionLogEntry(log)) {
            return true;
        }
        return getActionLogSeverity(log.event_name, log.payload) !== 'debug';
    });
}

/**
 * French descriptions per dispatch `event_name` (adapted from monitorix-frontend).
 * Tags `[Acteur]`/`[Régulateur]`/`[Chauffeur]` are resolved from the author (role-aware);
 * `[snake_case]` tokens (e.g. `[workflow_name]`) from the entry/payload.
 */
export const ACTION_LOG_EVENT_DESCRIPTIONS: Record<string, string> = {
    driver_accept: '[Chauffeur] a accepté la course',
    driver_reject: '[Chauffeur] a refusé la course',
    driver_waiting: '[Chauffeur] a modifié le statut en "Au point de RDV"',
    prebook_driver: "[Chauffeur] s'est prébooké sur la course",
    ride_started: '[Chauffeur] a modifié le statut en "Passager à bord"',
    ride_ended: '[Chauffeur] a modifié le statut en "Course terminée"',
    cancel: '[Acteur] a annulé la course',
    cancel_no_charge: '[Acteur] a annulé la course sans frais',
    cancel_with_charge: '[Acteur] a annulé la course avec frais',
    subcontract_manual: '[Régulateur] a sous-traité la course manuellement',
    dispatch_now: '[Acteur] a dispatché la course',
    unassign_driver: '[Acteur] a désaffecté le chauffeur de la course',
    'orchestrator.workflow.selected': 'Le workflow [workflow_name] a été sélectionné',
    'orchestrator.workflow.executed': 'Le workflow [workflow_name] a été exécuté',
    'orchestrator.workflow.stopped': 'Le workflow [workflow_name] a été arrêté',
    'workflow.kill.completed': 'Le workflow [workflow_name] est terminé',
    'orchestrator.dispatch-initiated': ACTION_LOG_LABELS.dispatchSearchStarted,
    'orchestrator.dispatch.failed': 'Le dispatch a échoué. Pas de chauffeur affecté à la course.',
    'orchestrator.dispatch.no-driver-alarm': "Aucun chauffeur n'est disponible",
    'orchestrator.dispatch-retry.terminal': 'Dispatch automatique arrêté.',
    'orchestrator.dispatch.resigned-candidates-exhausted':
        'Tous les chauffeurs contactés ont refusé ou laissé passer la course — dispatch abandonné',
    'orchestrator.dispatch-resume.started': 'Le système a repris le dispatch de la course',
    'orchestrator.dispatch-resume.failed': 'Le système a échoué la reprise du dispatch',
    [FLIGHTSTATS_ALERT_RECEIVED]: 'Alerte FlightStats reçue',
    [FLIGHTSTATS_DEPARTURE_UNCONFIRMED]: 'Départ du vol non confirmé par FlightStats : retard probable',
};

/** Dispatch events that mirror the legacy human-action history — always shown (not "technical"). */
export const MEANINGFUL_DISPATCH_EVENTS = new Set<string>([
    'driver_accept',
    'driver_reject',
    'driver_waiting',
    'prebook_driver',
    'ride_started',
    'ride_ended',
    'cancel',
    'cancel_no_charge',
    'cancel_with_charge',
    'subcontract_manual',
    'dispatch_now',
    'unassign_driver',
    FLIGHTSTATS_ALERT_RECEIVED,
    FLIGHTSTATS_DEPARTURE_UNCONFIRMED,
]);

/** "Technical" dispatch entries (orchestrator/workflow internals, debug, …) — hidden by default. */
export function isTechnicalActionLog(entry: ActionLogEntry): boolean {
    return !MEANINGFUL_DISPATCH_EVENTS.has(entry.event_name);
}

/** Human actor: author name first, else the local-part of `payload.operator_email`. */
export function resolveActionLogActorDisplay(
    author?: ActionLogEntryAuthor | null,
    payload?: unknown,
): string | undefined {
    const name = [author?.firstname, author?.lastname].filter(Boolean).join(' ').trim();
    if (name) {
        return name;
    }
    const p =
        payload && typeof payload === 'object' && !Array.isArray(payload)
            ? (payload as Record<string, unknown>)
            : undefined;
    const emailRaw = p?.operator_email ?? p?.operatorEmail;
    if (typeof emailRaw === 'string' && emailRaw.trim()) {
        const email = emailRaw.trim();
        const at = email.indexOf('@');
        return at > 0 ? email.slice(0, at) : email;
    }
    return undefined;
}

/**
 * Replace `[Acteur]`/`[Régulateur]`/`[Chauffeur]` tags with the actor `display` name when known —
 * the person who did the action is the person to show. Falls back to a generic French label when
 * no name is available (never raw tags).
 */
function applyActorNameToActionLogDescription(description: string, display?: string): string {
    return description
        .replace(/\[Acteur\]/g, display ?? 'Un opérateur')
        .replace(/\[Régulateur\]/g, display ?? 'Le régulateur')
        .replace(/\[Chauffeur\]/g, display ?? 'Le chauffeur');
}

/**
 * Meaningful, human-readable one-line description for a dispatch event.
 * `actorDisplay` overrides the actor name (e.g. borrowed from a legacy twin row when the dispatch
 * event's own `author` is empty); otherwise it's resolved from the entry's author/payload.
 */
export function describeActionLogEntry(entry: ActionLogEntry, actorDisplay?: string): string {
    const payload =
        entry.payload && typeof entry.payload === 'object' ? (entry.payload as Record<string, unknown>) : undefined;
    // Wave-level failure with an automatic retry scheduled — the map's definitive
    // "Le dispatch a échoué" is reserved for terminal failures (nextAction "none"/resign).
    if (entry.event_name === 'orchestrator.dispatch.failed' && payload?.nextAction === 'dispatch-retry') {
        return 'Vague de dispatch sans succès — pas de chauffeur affecté, nouvelle tentative programmée.';
    }
    if (entry.event_name === FLIGHTSTATS_ALERT_RECEIVED) {
        return describeFlightStatsAlert(entry.payload);
    }
    if (entry.event_name === FLIGHTSTATS_DEPARTURE_UNCONFIRMED) {
        return describeFlightStatsDepartureUnconfirmed(entry.payload);
    }
    // Driver-app cancel: the payload is the raw request body, so the reason is only there if sent.
    const cancelReason = payload?.cancel_reason ?? payload?.cancelReason ?? payload?.reason;
    const template =
        entry.event_name === 'cancel' && typeof cancelReason === 'string' && cancelReason
            ? `${ACTION_LOG_EVENT_DESCRIPTIONS.cancel} (Raison : ${cancelReason})`
            : (ACTION_LOG_EVENT_DESCRIPTIONS[entry.event_name] ??
              getActionLogRowUi(entry.event_name, entry.payload).eventColumnLabel);
    // Resolve [snake_case] tokens (e.g. [workflow_name]); actor tags are handled below.
    const withTokens = template.replace(/\[(\w+)\]/g, (match, key: string) => {
        if (key === 'Acteur' || key === 'Chauffeur') {
            return match;
        }
        const value = (entry as unknown as Record<string, unknown>)[key] ?? payload?.[key];
        return value != null && value !== '' ? String(value) : match;
    });
    const display = actorDisplay ?? resolveActionLogActorDisplay(entry.author, payload);
    return applyActorNameToActionLogDescription(withTokens, display);
}

/** UI label for the event source (orchestrator/workflow → "dispatch auto"). */
export function formatActionLogSource(source: string): string {
    if (source === 'orchestrator' || source === 'workflow') {
        return 'dispatch auto';
    }
    return source;
}
